import { createEvent, createStore, sample } from 'effector'
import { isNull } from 'lodash'
import { and, delay, not, or } from 'patronum'

import * as chat from '@/entities/chat'
import { $lang } from '@/shared/lib/i18n'
import { showErrorNotificationFx } from '@/shared/ui'

import * as api from '../api'
import { getChannelLang } from '../lib'
import { addChannelItem, processEvent } from './lib'
import * as channelModel from './model.channel'
import * as connModel from './model.conn'
import * as enterModel from './model.enter'
import * as muteModel from './model.mute'
import * as settingModel from './model.setting'
import * as slowmodeModel from './model.slowmode'
import * as userModel from './model.user'

export * from './model.channel'
export * from './model.conn'
export * from './model.enter'
export * from './model.message'
export * from './model.mute'
export * from './model.reaction'
export * from './model.setting'
export * from './model.slowmode'
export * from './model.translate'
export * from './model.unread'
export * from './model.user'

// --- Events ---

export const chatLoaded = createEvent()
export const chatChannelOpen = createEvent<{ channel: string }>()

sample({
    clock: chatChannelOpen,
    source: channelModel.$chatChannelList,
    // ensure that channel is not joined yet
    filter: (joined, { channel }) => joined.findIndex((chan) => chan.name === channel) === -1,
    fn: (_joined, { channel }): api.ChannelJoinRequest => ({ channel }),
    target: api.chatapi.channelJoin,
})

// --- Data flow & chat logic ---

// --- Session & user info ---

sample({
    // delay event to ensure that current channel is loaded from local storage
    clock: delay({ source: chatLoaded, timeout: 0 }),
    source: {
        userId: chat.$userId,
        portalLang: $lang,
        channelLang: channelModel.$chatChannel.map(getChannelLang),
    },
    // don't login if user is already logged in
    filter: ({ userId }) => userId === null,
    fn: ({ portalLang, channelLang }): api.LoginRequest => ({
        lang: channelLang ?? portalLang,
    }),
    target: userModel.loginFx,
})

sample({
    clock: userModel.loginFx.doneData,
    fn: ({ user }) => user,
    target: chat.$userInfo,
})

sample({
    clock: chat.$userInfo,
    fn: (userInfo) => userInfo?.token ?? null,
    target: api.chatapi.setToken,
})

sample({
    clock: userModel.loginFx.failData,
    fn: ({ message }) => ({ message: `Failed to login in chat: ${message}` }),
    target: showErrorNotificationFx,
})

// --- Centrifuge connection ---

sample({
    clock: userModel.loginFx.doneData,
    fn: ({ user: { token } }) => ({ token }),
    target: connModel.centrifugeConnectFx,
})

sample({
    clock: chat.userLogout,
    target: connModel.centrifugeDisconnectFx,
})

connModel.$chatItems.on(connModel.channelSubscribed, (items, { channel }) => ({
    ...items,
    [channel]: [],
}))

// --- Chat event handlers ---

connModel.$chatItems.on(chatChannelOpen, (items, { channel }) => ({
    ...items,
    [channel]: items[channel] || [],
}))

connModel.$chatItems.on(connModel.chatEventReceived, (items, { channel, event }) => ({
    ...items,
    [channel]: processEvent(items[channel] ?? [], event),
}))

connModel.$chatItems.reset(connModel.centrifugeDisconnected)

// --- Ready to chat ---

const $canInteractWithChat = and(
    // user completed enter list
    enterModel.$isEnterListComplete,
    // user is not muted
    not(muteModel.$isUserMuted),
    // maintenance mode is disabled
    not(settingModel.$isMaintenance)
)

sample({
    clock: or(
        // requirements for regular users
        and(
            // user is allowed to interact with chat
            $canInteractWithChat,
            // slowmode is not enabled
            slowmodeModel.$channelSlowmodeCooldown.map(isNull)
        ),
        // privileged users are always allowed to send messages
        chat.$userInfo.map(chat.isPrivilegedUser)
    ),
    target: userModel.$canSendMessages,
})

sample({
    clock: or(
        // user is allowed to interact with chat
        $canInteractWithChat,
        // privileged users are always allowed to leave message reactions
        chat.$userInfo.map(chat.isPrivilegedUser)
    ),
    target: userModel.$canReactToMessages,
})

// --- Welcome message ---

const enterListComplete = enterModel.$isEnterListComplete.updates.filter({ fn: (isReady) => isReady })
const historyReceived = channelModel.channelHistoryFx.done
const welcomeMessageSent = createEvent<{ channel: string }>()

// keep track of channels where welcome message was sent
const $welcomeMessageSentByChannel = createStore<Record<string, boolean>>({})
    .on(welcomeMessageSent, (state, { channel }) => ({ ...state, [channel]: true }))
    .reset(connModel.centrifugeDisconnected)

// keep track of channels where history was received
const $historyReceivedByChannel = createStore<Record<string, boolean>>({})
    .on(historyReceived, (state, { params: { channel } }) => ({ ...state, [channel]: true }))
    .reset(connModel.centrifugeDisconnected)

// check if we need to send welcome message
sample({
    clock: [
        // checkout welcome message when user switches to another channel
        chatChannelOpen,
        // checkout welcome message when history is received in current channel
        sample({
            clock: historyReceived,
            source: channelModel.$chatChannel,
            filter: (channel, { params: { channel: historyChannel } }) => channel === historyChannel,
            fn: (_, { params: { channel } }) => ({ channel }),
        }),
        // checkout welcome message when enter list is completed
        sample({ clock: enterListComplete, source: { channel: channelModel.$chatChannel } }),
    ],
    source: {
        isEnterListComplete: enterModel.$isEnterListComplete,
        historyReceived: $historyReceivedByChannel,
        welcomeMessageSent: $welcomeMessageSentByChannel,
    },
    filter: ({ isEnterListComplete, historyReceived, welcomeMessageSent }, { channel }): boolean => {
        // to see welcome message
        return (
            // enter list should be completed
            isEnterListComplete &&
            // history should be received
            historyReceived[channel] === true &&
            // welcome message should not be sent yet
            welcomeMessageSent[channel] !== true
        )
    },
    fn: (_, { channel }) => ({ channel }),
    target: welcomeMessageSent,
})

// add welcome message
sample({
    clock: welcomeMessageSent,
    source: connModel.$chatItems,
    fn: (items, { channel }) =>
        addChannelItem(channel, items, {
            type: 'message_welcome',
            payload: { channel },
        }),
    target: connModel.$chatItems,
})

// --- UNSORTED ---

sample({
    clock: chatChannelOpen,
    fn: ({ channel }) => channel,
    target: channelModel.$chatChannel,
})
